// Ported from Octo.Mobile@79a7c69 src/utils/spMessage.ts
import DOMPurify from 'dompurify';
/**
 * SP (RAISERROR / ProcessResult) mesajlarını düz metne çevirir.
 * Web bu mesajları HTML olarak bastığı için SP yazarları satır kırmak amacıyla
 * `[br]` belirteci ya da `<br/>` kullanıyor; mobilde ikisi de satır sonu olur.
 */
export function cleanSpMessage(text: string | null | undefined): string {
  if (!text) return '';
  return text
    .replace(/\r\n?/g, '\n')
    .replace(/\[br\s*\/?\]/gi, '\n')
    .replace(/<br\s*\/?>/gi, '\n')
    .replace(/\\r?\\n/g, '\n')
    .replace(/<[^>]*>/g, '')
    .replace(/&nbsp;/g, ' ')
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'")
    .trim();
}

/*
 * HTML'li SP mesajları (issue #446).
 *
 * Blazor RAISERROR mesajını HTML olarak basıyordu; SP'ler kalın başlık, renkli uyarı ve
 * madde listesi için buna güveniyor. Mesaj kullanıcı verisi de taşıyabildiği için yalnızca
 * biçim etiketleri geçer: betik, olay öznitelikleri, bağlantı hedefi, görsel, form, iframe
 * düşer. `style` içinden de yalnızca renk/yazı biçimi özellikleri kalır — konumlandırma
 * (`position:fixed` ile ekranı kaplayan sahte pencere) ya da arka plan görseli geçmez.
 */
const SP_HTML_TAGS = [
  'b',
  'strong',
  'i',
  'em',
  'u',
  's',
  'small',
  'sub',
  'sup',
  'mark',
  'code',
  'br',
  'hr',
  'p',
  'div',
  'span',
  'font',
  'ul',
  'ol',
  'li',
  'table',
  'thead',
  'tbody',
  'tr',
  'th',
  'td',
  'h1',
  'h2',
  'h3',
  'h4',
  'h5',
  'h6',
];
const SP_HTML_ATTRS = ['style', 'color', 'colspan', 'rowspan', 'align'];
const SP_STYLE_PROPS = new Set([
  'color',
  'background-color',
  'font-weight',
  'font-style',
  'font-size',
  'text-decoration',
  'text-align',
  'white-space',
]);
/** Stil değerinde url(), expression() gibi dış kaynak/ifade kalıpları. */
const UNSAFE_STYLE_VALUE = /url\s*\(|expression\s*\(|javascript:|@import|[<>]/i;

const TAG_RE = new RegExp(`</?(?:${SP_HTML_TAGS.join('|')})\\b[^>]*>`, 'i');

/** Metin, izin verilen biçim etiketlerinden en az birini taşıyor mu? (`<28>` gibi satır no'ları değil) */
export function hasSpHtml(text: string | null | undefined): boolean {
  return !!text && TAG_RE.test(text);
}

function filterStyle(style: string): string {
  return style
    .split(';')
    .map((decl) => {
      const idx = decl.indexOf(':');
      if (idx < 0) return null;
      const prop = decl.slice(0, idx).trim().toLowerCase();
      const value = decl.slice(idx + 1).trim();
      if (!SP_STYLE_PROPS.has(prop) || !value || UNSAFE_STYLE_VALUE.test(value)) return null;
      return `${prop}:${value}`;
    })
    .filter(Boolean)
    .join(';');
}

let hookInstalled = false;
function ensureStyleHook() {
  if (hookInstalled) return;
  hookInstalled = true;
  DOMPurify.addHook('uponSanitizeAttribute', (node, data) => {
    // Yalnızca bu yapılandırmayla çağrıldığında süz; DOMPurify'ın diğer kullanıcıları
    // (HTML editör önizlemesi) kendi kurallarıyla kalır.
    if (!sanitizing || data.attrName !== 'style') return;
    const filtered = filterStyle(data.attrValue);
    if (filtered) data.attrValue = filtered;
    else data.keepAttr = false;
    void node;
  });
}
let sanitizing = false;

/** SP mesajını güvenli HTML'e çevirir; `[br]` belirteci ve kaçışlı `\r\n` satır sonu olur. */
export function sanitizeSpHtml(text: string): string {
  ensureStyleHook();
  const prepared = text
    // Etiketler arasındaki biçimlendirme satır sonları boşluk olmasın (`<ul>\n<li>`).
    .replace(/>\s*\r?\n\s*</g, '><')
    .replace(/\[br\s*\/?\]/gi, '<br/>')
    .replace(/\\r?\\n/g, '<br/>')
    .replace(/\r?\n/g, '<br/>');
  sanitizing = true;
  try {
    return DOMPurify.sanitize(prepared, {
      ALLOWED_TAGS: SP_HTML_TAGS,
      ALLOWED_ATTR: SP_HTML_ATTRS,
      ALLOW_DATA_ATTR: false,
    });
  } finally {
    sanitizing = false;
  }
}

/*
 * Alan ipucu (Tb_Rn_Sys_ObjectFields.Tooltip) — #450.
 *
 * SP mesajlarının güvenli alt kümesine ek olarak BAĞLANTI geçer: dokümana "Detaylı bilgi"
 * yönlendirmesi için. Yalnız http(s) ve mailto adresleri kalır (javascript: vb. düşer); her
 * bağlantı yeni sekmede ve opener'sız açılır. Markdown biçimi `[metin](https://…)` de
 * bağlantıya çevrilir — Octo Lab'da HTML yazmadan bağlantı eklenebilsin.
 */
let linking = false;
let linkHookInstalled = false;
function ensureLinkHook() {
  if (linkHookInstalled) return;
  linkHookInstalled = true;
  DOMPurify.addHook('afterSanitizeAttributes', (node) => {
    if (!linking || node.nodeName !== 'A') return;
    const el = node as Element;
    const href = el.getAttribute('href') ?? '';
    if (!/^(https?:\/\/|mailto:)/i.test(href.trim())) el.removeAttribute('href');
    el.setAttribute('target', '_blank');
    el.setAttribute('rel', 'noopener noreferrer');
  });
}

const MARKDOWN_LINK = /\[([^\]\n]+)\]\(((?:https?:\/\/|mailto:)[^\s)]+)\)/gi;

export function sanitizeTooltipHtml(text: string): string {
  ensureStyleHook();
  ensureLinkHook();
  const prepared = text
    .replace(MARKDOWN_LINK, (_m, label: string, href: string) => `<a href="${href.replace(/"/g, '%22')}">${label}</a>`)
    .replace(/>\s*\r?\n\s*</g, '><')
    .replace(/\r\n|\r|\n/g, '<br/>');
  sanitizing = true;
  linking = true;
  try {
    return DOMPurify.sanitize(prepared, {
      ALLOWED_TAGS: [...SP_HTML_TAGS, 'a'],
      ALLOWED_ATTR: [...SP_HTML_ATTRS, 'href', 'target', 'rel'],
      ALLOW_DATA_ATTR: false,
    });
  } finally {
    sanitizing = false;
    linking = false;
  }
}
